import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Typography } from '@/components/ui/typography'
import { useBoardEdit } from '@/features/ogsm/board-edit-context'
import {
  ogsmDeleteDataAppeared,
  ogsmDeleteErrorMessage,
  ogsmDeleteHasData,
  ogsmDeleteRefused,
  ogsmErrorMessage,
} from '@/features/ogsm/errors'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { useOgsmApi } from '@/hooks/use-ogsm'
import {
  ogsmScopeKeys,
  useArchiveOgsmNodeMutation,
  useDeleteOgsmNodeMutation,
  useOgsmRemovalImpactQuery,
} from '@/hooks/use-ogsm-data'
import type { OgsmBoardNode, OgsmRemovalImpact } from '@/types/api'

/**
 * Asks first, after reading what a removal would do. No data: a plain DELETE.
 * Has data: what is recorded, then "Confirm delete" (an archive the copy never names).
 * A refusal no retry can fix offers only Close; a 5xx keeps the retry.
 */
export function DeleteNodeDialog({
  node,
  kind,
  open,
  onOpenChange,
}: {
  node: OgsmBoardNode
  kind: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* DialogContent is a grid with gap-5: header, body, reason and buttons take no margins of their own. */}
      <DialogContent className="max-w-105 px-4 py-5">
        {/* Radix unmounts this on close, so the impact read and both mutations start fresh on every open. */}
        <DeleteNodeBody
          node={node}
          kind={kind}
          close={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  )
}

const RECORDED_ORDER = [
  'readings',
  'metricActuals',
  'targetRevisions',
  'scorecardKpis',
] as const

function RecordedSoFar({ impact }: { impact: OgsmRemovalImpact }) {
  const lines = RECORDED_ORDER.filter((key) => impact.counts[key] > 0)
  return (
    <div className="flex flex-col gap-3.5">
      {lines.length > 0 && (
        <div className="flex flex-col">
          <Typography variant="overline" className="mb-1 text-primary/60">
            {OGSM_COPY.recordedSoFar}
          </Typography>
          <ul>
            {lines.map((key) => (
              <li key={key} className="border-b border-primary/10 py-1.5">
                <Typography
                  as="span"
                  variant="body"
                  className="font-normal tabular-nums text-primary"
                >
                  {OGSM_COPY.recordedLine[key](impact.counts[key])}
                </Typography>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex flex-col gap-1 rounded-md border border-primary/10 bg-primary/5 px-3 py-2.5">
        <Typography variant="body-sm" className="text-primary/60">
          {OGSM_COPY.deleteKeptNote}
        </Typography>
        {impact.counts.metricActuals > 0 && (
          <Typography variant="body-sm" className="text-primary/60">
            {OGSM_COPY.metricActualsNote(impact.metricActualsShared)}
          </Typography>
        )}
      </div>
    </div>
  )
}

function DeleteNodeBody({
  node,
  kind,
  close,
}: {
  node: OgsmBoardNode
  kind: string
  close: () => void
}) {
  const { companyId, session } = useBoardEdit()
  const queryClient = useQueryClient()
  const api = useOgsmApi(companyId)
  const impactQuery = useOgsmRemovalImpactQuery(api, node.id)
  const remove = useDeleteOgsmNodeMutation(api, session.id)
  const archive = useArchiveOgsmNodeMutation(api, session.id)
  const closeRef = useRef<HTMLButtonElement>(null)
  const keepRef = useRef<HTMLButtonElement>(null)
  const primaryRef = useRef<HTMLButtonElement>(null)

  const appeared = ogsmDeleteHasData(remove.error)
  const impact = appeared ?? impactQuery.data
  const hasData = impact?.outcome === 'ARCHIVE'
  const active = hasData ? archive : remove
  const rereadNeeded = ogsmDeleteDataAppeared(remove.error) && !appeared
  const { reset: resetRemove } = remove

  const refusal = [remove.error, archive.error, impactQuery.error].find(
    ogsmDeleteRefused
  )
  const blocked = impact?.outcome === 'BLOCKED' ? impact : null
  const refused = refusal != null || blocked !== null
  const checkFailed =
    !refused && !appeared && impactQuery.isError && !impactQuery.isFetching
  const checking =
    !refused &&
    !checkFailed &&
    !impact &&
    (impactQuery.isPending || impactQuery.isFetching)
  const retryable =
    !refused && active.error !== null && !ogsmDeleteDataAppeared(active.error)

  // A has-data 409 without a usable impact body: read the impact again (back to "checking").
  useEffect(() => {
    if (!rereadNeeded) return
    resetRemove()
    void queryClient.resetQueries({
      queryKey: ogsmScopeKeys(api).removalImpact(node.id),
    })
  }, [rereadNeeded, resetRemove, queryClient, api, node.id])

  // The focused primary unmounts on a refusal; Radix then focuses the dialog itself.
  useEffect(() => {
    if (refused) closeRef.current?.focus()
  }, [refused])

  // A pending primary is disabled and drops focus; a retry or the new decision gets it back.
  useEffect(() => {
    if (retryable || appeared) primaryRef.current?.focus()
  }, [retryable, appeared])

  const refusalText =
    refusal != null
      ? ogsmDeleteErrorMessage(refusal, kind)
      : blocked?.blockedBy
        ? OGSM_COPY.deleteBlocked(blocked.blockedBy, kind)
        : ogsmErrorMessage(undefined)

  return (
    <>
      <DialogHeader>
        <DialogTitle>{OGSM_COPY.deleteTitle(kind)}</DialogTitle>
        <DialogDescription>
          {hasData && !refused && impact
            ? OGSM_COPY.deleteHasDataBody(node.title, impact.descendantCount)
            : OGSM_COPY.deleteBody(node.title)}
        </DialogDescription>
      </DialogHeader>
      {checking && (
        <Typography variant="body-sm" className="text-primary/60" role="status">
          {OGSM_COPY.checkingForData}
        </Typography>
      )}
      {checkFailed && (
        <div className="flex flex-wrap items-baseline gap-x-1">
          <Typography
            as="span"
            variant="body-sm"
            className="text-destructive"
            role="alert"
          >
            {OGSM_COPY.checkForDataFailed(kind)}
          </Typography>
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-xs"
            onClick={() => {
              // The link unmounts while it reloads; keep focus in the dialog.
              keepRef.current?.focus()
              void impactQuery.refetch()
            }}
          >
            {OGSM_COPY.tryAgain}
          </Button>
        </div>
      )}
      {hasData && !refused && impact && <RecordedSoFar impact={impact} />}
      {(refused || retryable) && (
        <Typography variant="body-sm" role="alert" className="text-destructive">
          {refused ? refusalText : ogsmErrorMessage(active.error)}
        </Typography>
      )}
      <div className="flex justify-end gap-2">
        {refused ? (
          <Button variant="outline" ref={closeRef} onClick={close}>
            {OGSM_COPY.close}
          </Button>
        ) : (
          <>
            <Button
              variant="outline"
              ref={keepRef}
              disabled={active.isPending}
              onClick={close}
            >
              {OGSM_COPY.keepIt}
            </Button>
            <Button
              ref={primaryRef}
              variant="destructive"
              className="bg-destructive text-white"
              disabled={!impact || active.isPending}
              onClick={() => active.mutate(node.id, { onSuccess: close })}
            >
              {active.isPending
                ? OGSM_COPY.deleting
                : hasData
                  ? OGSM_COPY.confirmDelete
                  : OGSM_COPY.delete}
            </Button>
          </>
        )}
      </div>
    </>
  )
}
